import { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import {
  isAdmin, logAdminAction,
  sendPasswordReset, suspendUser, unsuspendUser,
} from '../lib/admin';
import { isEligibleForBadge, BADGE_PRICE_USD } from '../lib/verifiedBadge';

export default function Admin({ user }) {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [tab, setTab] = useState('users');

  const [managers, setManagers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [suspensions, setSuspensions] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userFilter, setUserFilter] = useState('');
  const [managerFilter, setManagerFilter] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [reviewFilter, setReviewFilter] = useState('');
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [toast, setToast] = useState(null);

  // Gate: must be logged in AND have a row in admins.
  useEffect(() => {
    (async () => {
      if (!user?.id) { setChecking(false); return; }
      const ok = await isAdmin(user.id);
      setAllowed(ok);
      setChecking(false);
    })();
  }, [user]);

  useEffect(() => {
    if (!allowed) return;
    (async () => {
      setLoading(true);
      const [mgrsR, tenR, revsR, profR, suspR, auditR] = await Promise.all([
        supabase.from('managers').select('*').order('created_at', { ascending: false }),
        supabase.from('tenants').select('*').order('created_at', { ascending: false }),
        supabase.from('reviews').select('*').order('created_at', { ascending: false }),
        supabase.from('profiles').select('*').order('created_at', { ascending: false }),
        supabase.from('user_suspensions').select('*'),
        supabase.from('admin_audit_log').select('*').order('created_at', { ascending: false }).limit(100),
      ]);
      setManagers(mgrsR.data || []);
      setTenants(tenR.data || []);
      setReviews(revsR.data || []);
      setProfiles(profR.data || []);
      setSuspensions(suspR.data || []);
      setAuditLog(auditR.data || []);
      setLoading(false);
    })();
  }, [allowed]);

  const flash = (text, type = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3000);
  };

  const refreshSuspensions = async () => {
    const { data } = await supabase.from('user_suspensions').select('*');
    setSuspensions(data || []);
  };
  const refreshAudit = async () => {
    const { data } = await supabase.from('admin_audit_log').select('*').order('created_at', { ascending: false }).limit(100);
    setAuditLog(data || []);
  };

  // ─── Admin actions ───
  const handleSendReset = async (p) => {
    const { error } = await sendPasswordReset({ email: p.email });
    if (error) { flash(error.message, 'error'); return; }
    await logAdminAction({
      adminId: user.id, adminEmail: user.email,
      action: 'send_password_reset',
      targetType: 'user', targetId: p.id, targetEmail: p.email,
    });
    flash(`Password-reset email sent to ${p.email}.`);
    refreshAudit();
  };

  const handleSuspend = async (p) => {
    const reason = window.prompt(`Suspend ${p.email}? Optional reason shown to the user:`, '');
    if (reason === null) return;
    const { error } = await suspendUser({ userId: p.id, email: p.email, reason, adminId: user.id });
    if (error) { flash(error.message, 'error'); return; }
    await logAdminAction({
      adminId: user.id, adminEmail: user.email,
      action: 'suspend_user',
      targetType: 'user', targetId: p.id, targetEmail: p.email,
      details: { reason },
    });
    flash(`${p.email} suspended.`);
    refreshSuspensions(); refreshAudit();
  };

  const handleUnsuspend = async (p) => {
    if (!window.confirm(`Re-enable ${p.email}?`)) return;
    const { error } = await unsuspendUser({ userId: p.id });
    if (error) { flash(error.message, 'error'); return; }
    await logAdminAction({
      adminId: user.id, adminEmail: user.email,
      action: 'unsuspend_user',
      targetType: 'user', targetId: p.id, targetEmail: p.email,
    });
    flash(`${p.email} re-enabled.`);
    refreshSuspensions(); refreshAudit();
  };

  // TODO Phase 2: replace manual grant with a Stripe webhook that flips
  // verified_badge=true when the $49.99/yr subscription is paid, and
  // false when it lapses. Admin button stays as a manual override.
  const handleToggleBadge = async (m, force = false) => {
    const next = !m.verified_badge;
    const verb = next ? 'Grant' : 'Revoke';
    if (next) {
      const elig = isEligibleForBadge(m);
      if (!elig.eligible && !force) {
        const msg = `${m.name} does NOT meet badge eligibility:\n\n- ${elig.failing.join('\n- ')}\n\nGrant anyway (override)?`;
        if (!window.confirm(msg)) return;
      } else if (elig.eligible) {
        if (!window.confirm(`Grant Verified badge to ${m.name}? Normally tied to a $${BADGE_PRICE_USD}/yr payment — only do this manually when you've confirmed payment outside the app.`)) return;
      }
    } else {
      if (!window.confirm(`Revoke the Verified badge for ${m.name}?`)) return;
    }
    const { error } = await supabase.from('managers').update({ verified_badge: next }).eq('id', m.id);
    if (error) { flash(error.message, 'error'); return; }
    setManagers(prev => prev.map(x => x.id === m.id ? { ...x, verified_badge: next } : x));
    await logAdminAction({
      adminId: user.id, adminEmail: user.email,
      action: next ? 'grant_verified_badge' : 'revoke_verified_badge',
      targetType: 'manager', targetId: m.id,
      details: { manager_name: m.name, annual_price_usd: 49.99 },
    });
    flash(`${m.name} ${next ? 'now has' : 'no longer has'} the Verified badge.`);
    refreshAudit();
  };

  const handleDeleteReview = async (r) => {
    if (!window.confirm('Delete this review?')) return;
    const { error } = await supabase.from('reviews').delete().eq('id', r.id);
    if (error) { flash(error.message, 'error'); return; }
    setReviews(prev => prev.filter(x => x.id !== r.id));
    await logAdminAction({
      adminId: user.id, adminEmail: user.email,
      action: 'delete_review',
      targetType: 'review', targetId: r.id,
      details: { author: r.author, profile_id: r.profile_id, rating: r.rating },
    });
    flash('Review deleted.');
    refreshAudit();
  };

  // ─── Gates ───
  if (checking) return <Center>Checking access…</Center>;
  if (!user) return (
    <Center>
      <h2 style={h2}>Admin access</h2>
      <p style={muted}>Sign in to your admin account first.</p>
      <button onClick={() => navigate('/login?next=/admin')} style={btnPrimary}>Sign in</button>
    </Center>
  );
  if (!allowed) return (
    <Center>
      <h2 style={h2}>Not authorized</h2>
      <p style={muted}>
        Signed in as <b>{user.email}</b>, but this account has no admin role.
        Ask another admin to add you, or run in Supabase SQL editor:
      </p>
      <pre style={pre}>{`insert into public.admins (user_id, notes)
values ((select id from auth.users where email = '${user.email}'), 'note');`}</pre>
    </Center>
  );

  const suspendedByUser = Object.fromEntries(suspensions.map(s => [s.user_id, s]));
  const filteredProfiles = profiles.filter(p =>
    !userFilter || p.email?.toLowerCase().includes(userFilter.toLowerCase()) || p.name?.toLowerCase().includes(userFilter.toLowerCase())
  );

  return (
    <div style={{ minHeight: '100vh', background: '#F5EFE3' }}>
      {/* top bar */}
      <div style={{ background: '#0F2C4E', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 22, color: '#fff' }}>
          Renter<span style={{ color: '#C9972A' }}>Rep</span>
          <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginLeft: 10, fontFamily: 'sans-serif' }}>Admin · {user.email}</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => navigate('/')} style={topBtnGhost}>View site</button>
        </div>
      </div>

      {/* Persistent stat cards — always visible, click to switch tab below */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '20px 16px 0' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
          <Stat icon="👥" label="Users" value={profiles.length} color="#1A4C8B" active={tab === 'users'} onClick={() => setTab('users')} />
          <Stat icon="🏢" label="Property Management" value={managers.length} color="#1A4C8B" active={tab === 'managers'} onClick={() => setTab('managers')} />
          <Stat icon="🧍" label="Tenants" value={tenants.length} color="#1A4C8B" active={tab === 'tenants'} onClick={() => setTab('tenants')} />
          <Stat icon="⭐" label="Reviews" value={reviews.length} color="#C9972A" active={tab === 'reviews'} onClick={() => setTab('reviews')} />
          <Stat icon="🚫" label="Suspended" value={suspensions.length} color="#C0392B" active={tab === 'users'} onClick={() => setTab('users')} />
          <Stat icon="📜" label="Audit log" value={auditLog.length} color="#6B6358" active={tab === 'audit'} onClick={() => setTab('audit')} />
        </div>
      </div>

      {/* tabs */}
      <div style={{ background: '#FAF6EC', borderBottom: '1px solid rgba(184,137,58,0.22)', padding: '0 24px', display: 'flex', gap: 4, overflowX: 'auto', marginTop: 18 }}>
        {[
          ['users', `Users (${profiles.length})`],
          ['managers', `Property Management (${managers.length})`],
          ['tenants', `Tenants (${tenants.length})`],
          ['reviews', `Reviews (${reviews.length})`],
          ['audit', 'Audit log'],
        ].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '14px 16px', fontSize: 13, fontWeight: tab === key ? 700 : 500,
            color: tab === key ? '#0F2C4E' : '#6C757D',
            borderBottom: tab === key ? '2px solid #C9972A' : '2px solid transparent',
            whiteSpace: 'nowrap',
          }}>{label}</button>
        ))}
      </div>

      {toast && (
        <div style={{ padding: '10px 24px', background: toast.type === 'error' ? '#FDEDEB' : '#E8F5EE', color: toast.type === 'error' ? '#C0392B' : '#1B7A4B', fontSize: 13, fontWeight: 600 }}>
          {toast.type === 'error' ? '⚠️ ' : '✓ '}{toast.text}
        </div>
      )}

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px' }}>

        {tab === 'users' && (
          <>
            <h2 style={sectionH2}>Users</h2>
            <p style={{ fontSize: 12, color: '#6C757D', marginBottom: 14 }}>
              List is populated when users sign up. Existing accounts created before the profiles table was added will not appear here.
            </p>
            <input
              value={userFilter}
              onChange={e => setUserFilter(e.target.value)}
              placeholder="Filter by email or name…"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(184,137,58,0.45)', background: '#FAF6EC', color: '#0F2C4E', marginBottom: 14, fontSize: 13 }}
            />
            {loading ? <Loading /> :
              filteredProfiles.length === 0
                ? <Empty>No users match.</Empty>
                : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {filteredProfiles.map(p => {
                      const susp = suspendedByUser[p.id];
                      const expanded = expandedUserId === p.id;
                      const theirReviews = reviews.filter(r => r.author && p.name && r.author.toLowerCase() === p.name.toLowerCase());
                      const linkedManager = managers.find(m => m.name && p.name && m.name.toLowerCase() === p.name.toLowerCase());
                      const linkedTenant  = tenants.find(t => t.name && p.name && t.name.toLowerCase() === p.name.toLowerCase());
                      return (
                        <div key={p.id} style={{ background: '#FAF6EC', borderRadius: 10, boxShadow: '0 1px 4px rgba(120,90,30,0.08)', border: '1px solid rgba(184,137,58,0.22)' }}>
                          <div style={{ ...userRow, boxShadow: 'none', borderRadius: expanded ? '10px 10px 0 0' : 10, cursor: 'pointer' }} onClick={() => setExpandedUserId(expanded ? null : p.id)}>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 14, fontWeight: 600, color: '#0F2C4E' }}>{p.name || '(no name)'}</span>
                                <span style={{ fontSize: 11, color: '#0F2C4E', background: 'rgba(184,137,58,0.18)', padding: '2px 8px', borderRadius: 20, textTransform: 'capitalize', fontWeight: 600 }}>{p.role || 'tenant'}</span>
                                {susp && <span style={{ fontSize: 11, color: '#C0392B', background: '#FDEDEB', border: '1px solid #C0392B', padding: '2px 8px', borderRadius: 20, fontWeight: 700 }}>SUSPENDED</span>}
                                <span style={{ fontSize: 11, color: '#6C757D' }}>{expanded ? '▾' : '▸'}</span>
                              </div>
                              <div style={{ fontSize: 12, color: '#6C757D', marginTop: 2 }}>{p.email} · joined {new Date(p.created_at).toLocaleDateString()} · {theirReviews.length} review{theirReviews.length === 1 ? '' : 's'}</div>
                            </div>
                            <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                              <button onClick={() => handleSendReset(p)} style={btnGhost}>Send reset</button>
                              {susp
                                ? <button onClick={() => handleUnsuspend(p)} style={btnGreen}>Re-enable</button>
                                : <button onClick={() => handleSuspend(p)} style={btnRed}>Suspend</button>
                              }
                            </div>
                          </div>
                          {expanded && (
                            <div style={{ padding: '14px 16px', borderTop: '1px solid rgba(184,137,58,0.22)', background: '#F5EFE3' }}>
                              <DetailGrid rows={[
                                ['Email', p.email],
                                ['Name', p.name || '—'],
                                ['Role', p.role || '—'],
                                ['User ID', p.id],
                                ['Joined', new Date(p.created_at).toLocaleString()],
                                ['Suspended', susp ? `Yes — ${susp.reason || '(no reason)'} (${new Date(susp.suspended_at).toLocaleDateString()})` : 'No'],
                                ['Linked manager profile', linkedManager ? linkedManager.name + (linkedManager.address ? ` · ${linkedManager.address}` : '') : '—'],
                                ['Linked tenant profile', linkedTenant  ? linkedTenant.name  + (linkedTenant.address  ? ` · ${linkedTenant.address}`  : '') : '—'],
                              ]} />
                              {theirReviews.length > 0 && (
                                <>
                                  <div style={{ fontSize: 12, fontWeight: 700, color: '#0F2C4E', marginTop: 12, marginBottom: 6 }}>Recent reviews by this user</div>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    {theirReviews.slice(0, 5).map(r => (
                                      <div key={r.id} style={{ fontSize: 12, color: '#343A40', padding: '6px 10px', background: '#FAF6EC', borderRadius: 6, border: '1px solid rgba(184,137,58,0.22)' }}>
                                        <b>{'⭐'.repeat(r.rating || 0)}</b> · {r.date} · {r.profile_type} · <span style={{ color: '#6C757D' }}>{(r.text || '').slice(0, 100)}{r.text?.length > 100 ? '…' : ''}</span>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                              {(linkedManager || linkedTenant) && (
                                <div style={{ marginTop: 10 }}>
                                  <button
                                    onClick={() => navigate('/profile/' + (linkedManager?.id || linkedTenant?.id))}
                                    style={btnGhost}
                                  >
                                    View public profile →
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
            }
          </>
        )}

        {tab === 'managers' && (
          <>
            <h2 style={sectionH2}>Property Management ({managers.length})</h2>
            <input
              value={managerFilter}
              onChange={e => setManagerFilter(e.target.value)}
              placeholder="Filter by name or address…"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(184,137,58,0.45)', background: '#FAF6EC', color: '#0F2C4E', marginBottom: 14, fontSize: 13 }}
            />
            {loading ? <Loading /> : (() => {
              const filtered = managers.filter(m =>
                !managerFilter
                || m.name?.toLowerCase().includes(managerFilter.toLowerCase())
                || m.address?.toLowerCase().includes(managerFilter.toLowerCase())
              );
              if (filtered.length === 0) return <Empty>No managers match.</Empty>;
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {filtered.map(m => {
                    const elig = isEligibleForBadge(m);
                    return (
                      <div key={m.id} style={userRow}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 14, fontWeight: 600, color: '#0F2C4E' }}>{m.name}</span>
                            {m.verified_badge && (
                              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.06em', color: '#fff', background: '#C9972A', padding: '2px 8px', borderRadius: 20 }}>
                                ★ VERIFIED
                              </span>
                            )}
                            <span
                              title={elig.eligible ? 'Meets all badge eligibility rules' : elig.failing.join(' · ')}
                              style={{
                                fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
                                color: elig.eligible ? '#1B7A4B' : '#C0392B',
                                background: elig.eligible ? '#E8F5EE' : '#FDEDEB',
                                border: '1px solid ' + (elig.eligible ? '#1B7A4B' : '#C0392B'),
                                padding: '2px 8px', borderRadius: 20,
                              }}
                            >
                              {elig.eligible ? '✓ ELIGIBLE' : '✗ NOT ELIGIBLE'}
                            </span>
                          </div>
                          <div style={{ fontSize: 12, color: '#6B6358', marginTop: 2 }}>
                            {m.address || 'No address'} · ⭐ {m.rating || 0} · {m.review_count || 0} review{m.review_count === 1 ? '' : 's'}
                          </div>
                          {!elig.eligible && (
                            <div style={{ fontSize: 11, color: '#C0392B', marginTop: 2 }}>
                              Failing: {elig.failing.join(' · ')}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap' }}>
                          <button
                            onClick={() => handleToggleBadge(m)}
                            title={m.verified_badge ? 'Revoke badge' : `Verified badge — $${BADGE_PRICE_USD}/yr. ${elig.eligible ? 'Eligible.' : 'Not eligible — admin override required.'}`}
                            style={m.verified_badge ? btnRed : { ...btnGhost, background: '#C9972A', color: '#fff', opacity: elig.eligible ? 1 : 0.75 }}
                          >
                            {m.verified_badge
                              ? 'Revoke badge'
                              : (elig.eligible ? `★ Grant badge ($${BADGE_PRICE_USD}/yr)` : '★ Grant (override)')}
                          </button>
                          <button onClick={() => navigate('/profile/' + m.id)} style={btnGhost}>View profile</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </>
        )}

        {tab === 'tenants' && (
          <>
            <h2 style={sectionH2}>Tenants ({tenants.length})</h2>
            <input
              value={tenantFilter}
              onChange={e => setTenantFilter(e.target.value)}
              placeholder="Filter by name…"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(184,137,58,0.45)', background: '#FAF6EC', color: '#0F2C4E', marginBottom: 14, fontSize: 13 }}
            />
            {loading ? <Loading /> : (() => {
              const filtered = tenants.filter(t =>
                !tenantFilter
                || t.name?.toLowerCase().includes(tenantFilter.toLowerCase())
              );
              if (filtered.length === 0) return <Empty>No tenants match.</Empty>;
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {filtered.map(t => (
                    <div key={t.id} style={userRow}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#0F2C4E' }}>{t.name}</div>
                        <div style={{ fontSize: 12, color: '#6C757D', marginTop: 2 }}>
                          {t.phone || ''} {t.about ? `· ${t.about.slice(0, 80)}` : ''}
                        </div>
                      </div>
                      <button onClick={() => navigate('/profile/' + t.id)} style={btnGhost}>View profile</button>
                    </div>
                  ))}
                </div>
              );
            })()}
          </>
        )}

        {tab === 'reviews' && (
          <>
            <h2 style={sectionH2}>Reviews ({reviews.length})</h2>
            <input
              value={reviewFilter}
              onChange={e => setReviewFilter(e.target.value)}
              placeholder="Filter by author, text, or profile type (manager / tenant)…"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(184,137,58,0.45)', background: '#FAF6EC', color: '#0F2C4E', marginBottom: 14, fontSize: 13 }}
            />
            {loading ? <Loading /> :
              reviews
                .filter(r => {
                  if (!reviewFilter) return true;
                  const q = reviewFilter.toLowerCase();
                  return (r.author || '').toLowerCase().includes(q)
                      || (r.text || '').toLowerCase().includes(q)
                      || (r.profile_type || '').toLowerCase().includes(q);
                })
                .map(r => (
                <div key={r.id} style={{ background: '#FAF6EC', borderRadius: 10, padding: 16, marginBottom: 10, boxShadow: '0 1px 4px rgba(120,90,30,0.08)', border: '1px solid rgba(184,137,58,0.22)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#0F2C4E' }}>{r.author}</span>
                        <span style={{ fontSize: 12, color: '#C9972A' }}>{'⭐'.repeat(r.rating || 0)}</span>
                        <span style={{ fontSize: 11, color: '#6B6358' }}>{r.date}</span>
                        <span style={{ background: 'rgba(184,137,58,0.18)', color: '#0F2C4E', fontSize: 10, padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>{r.profile_type}</span>
                      </div>
                      <div style={{ fontSize: 13, color: '#343A40', lineHeight: 1.6 }}>{r.text || '(no written review)'}</div>
                    </div>
                    <button onClick={() => handleDeleteReview(r)} style={btnRed}>Delete</button>
                  </div>
                </div>
              ))
            }
          </>
        )}

        {tab === 'audit' && (
          <>
            <h2 style={sectionH2}>Audit log (last 100)</h2>
            {loading ? <Loading /> :
              auditLog.length === 0
                ? <Empty>No admin actions yet.</Empty>
                : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {auditLog.map(a => (
                      <div key={a.id} style={{ background: '#FAF6EC', borderRadius: 8, padding: 12, boxShadow: '0 1px 3px rgba(120,90,30,0.06)', border: '1px solid rgba(184,137,58,0.22)', fontSize: 12, color: '#343A40' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                          <span><b>{a.action}</b> · {a.admin_email}</span>
                          <span style={{ color: '#6C757D' }}>{new Date(a.created_at).toLocaleString()}</span>
                        </div>
                        <div style={{ fontSize: 11, color: '#6C757D', marginTop: 2 }}>
                          target: {a.target_type} {a.target_email || a.target_id || ''}
                          {a.details && <> · {JSON.stringify(a.details)}</>}
                        </div>
                      </div>
                    ))}
                  </div>
            }
          </>
        )}
      </div>
    </div>
  );
}

function Center({ children }) {
  return (
    <div style={{ minHeight: '80vh', background: '#F5EFE3', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: '#FAF6EC', borderRadius: 12, padding: 28, maxWidth: 520, width: '100%', boxShadow: '0 2px 8px rgba(120,90,30,0.10)', border: '1px solid rgba(184,137,58,0.22)', textAlign: 'center' }}>
        {children}
      </div>
    </div>
  );
}
function Stat({ icon, label, value, color, onClick, active }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: '#FAF6EC', borderRadius: 12, padding: 14,
        boxShadow: active ? '0 4px 12px rgba(120,90,30,0.18)' : '0 1px 4px rgba(120,90,30,0.08)',
        borderTop: '2px solid ' + (active ? '#C9972A' : 'rgba(184,137,58,0.22)'),
        borderRight: '2px solid ' + (active ? '#C9972A' : 'rgba(184,137,58,0.22)'),
        borderBottom: '2px solid ' + (active ? '#C9972A' : 'rgba(184,137,58,0.22)'),
        borderLeft: '2px solid ' + (active ? '#C9972A' : 'rgba(184,137,58,0.22)'),
        textAlign: 'left',
        cursor: onClick ? 'pointer' : 'default', width: '100%',
        transition: 'all 0.15s',
      }}
    >
      <div style={{ fontSize: 18, marginBottom: 4 }}>{icon}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 11, color: '#6B6358', marginTop: 2 }}>{label}</div>
    </button>
  );
}
function DetailGrid({ rows }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', rowGap: 6, columnGap: 12, fontSize: 12 }}>
      {rows.map(([k, v]) => (
        <Fragment key={k}>
          <div style={{ color: '#6C757D' }}>{k}</div>
          <div style={{ color: '#0F2C4E', fontWeight: 500, wordBreak: 'break-word' }}>{v}</div>
        </Fragment>
      ))}
    </div>
  );
}
function Loading() { return <div style={{ padding: 24, textAlign: 'center', color: '#6C757D' }}>Loading…</div>; }
function Empty({ children }) { return <div style={{ padding: 24, textAlign: 'center', color: '#6C757D', fontSize: 13 }}>{children}</div>; }

const h2 = { fontFamily: "'DM Serif Display', serif", fontSize: 22, color: '#0F2C4E', fontWeight: 400, marginBottom: 8 };
const muted = { fontSize: 13, color: '#6B6358', lineHeight: 1.6, marginBottom: 12 };
const pre = { background: '#F5EFE3', padding: 10, borderRadius: 8, fontSize: 11, textAlign: 'left', whiteSpace: 'pre-wrap', wordBreak: 'break-word', border: '1px solid rgba(184,137,58,0.22)' };
const sectionH2 = { fontSize: 18, fontWeight: 700, color: '#0F2C4E', marginBottom: 18 };
const btnPrimary = { background: '#0F2C4E', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 50, fontSize: 13, fontWeight: 700, cursor: 'pointer' };
const btnGhost   = { background: 'rgba(184,137,58,0.18)', border: 'none', color: '#0F2C4E', padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const btnRed     = { background: '#FDEDEB', border: 'none', color: '#C0392B', padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const btnGreen   = { background: '#E8F5EE', border: 'none', color: '#1B7A4B', padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const topBtnGhost = { background: 'transparent', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', padding: '6px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer' };
const userRow = { background: '#FAF6EC', borderRadius: 10, padding: 14, boxShadow: '0 1px 4px rgba(120,90,30,0.08)', border: '1px solid rgba(184,137,58,0.22)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' };
